import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowClockwise, CaretLeft, CaretRight, Car, Check, Door, DoorOpen, DotsThree, Handshake, PaperPlaneTilt, PencilSimple, SlidersHorizontal, SquaresFour, Storefront,
  Trash, Warehouse, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, MenueEintrag, Popover, SegmentedControl, SlidePanel, WidgetGroup } from '../../../../components/immobilien/ui';
import { ANGEBUNDENE_BEREICHE, BEREICH_KOPF, EINHEIT_WIDGETS, istBereichKey, type EinheitBereichKey } from '../../../../components/immobilien/einheitWidgets';
import { GRUPPEN, gruppenAnzahl, widgetsDerGruppe, type GruppenFilter } from '../../../../lib/immobilien/widgetRaster';
import { useWidgetSichtbarkeit } from '../../../../lib/immobilien/widgetSichtbarkeit';
import { useEinheitDetail, type EinheitBereich, type MietverhaeltnisRoh } from '../../../../lib/immobilien/useEinheitDetail';
import { einheitWidgetAngeboten, flaecheDerEinheit, laufendeMietverhaeltnisse, mieteAktuell, mietverhaeltnisse } from '../../../../lib/immobilien/einheitDetail';
import { einheitArt, type EinheitArt } from '../../../../lib/immobilien/objektDetail';
import { objektName } from '../../../../lib/immobilien/objektKopf';
import { verwaltungsartVonObjekt } from '../../../../lib/immobilien/sidebarNav';
import { meldeObjektGeaendert } from '../../../../lib/immobilien/useObjektKontext';
import { heuteIso } from '../../../../lib/immobilien/format';
import { supabase } from '../../../../lib/supabase/client';
import { loeschFehlerText } from '../../../../lib/immobilien/zimmer';
import { schreibenPfad } from '../../../../lib/schreiben/einstieg';
import { EinheitZimmer } from './EinheitZimmer';
import { GRUPPEN_ICONS } from '../objekt/ObjektSeite';
import { KopfKnopf } from '../objekt/ObjektHero';
import { WidgetBearbeiten } from '../objekt/WidgetBearbeiten';
import { EinheitSeitenKontext, type EinheitSeitenWert } from './EinheitSeitenKontext';
import { EinheitWidget } from './EinheitWidgets';
import { BereichInhalt } from './bereiche/BereichInhalt';
import { MietverhaeltnisBereich } from './bereiche/MietverhaeltnisBereich';
import { EinheitBearbeitenBereich, type EinheitReiter } from './bereiche/EinheitBearbeitenBereich';


const istEinheitReiter = (r: string | null) => r === 'einheit' || r === 'ausstattung' || r === 'kauf' || r === 'finanzierung' || r === 'abrechnung';

const ART: Record<EinheitArt, { label: string; icon: Icon }> = {
  wohnung: { label: 'Wohneinheit', icon: Door },
  gewerbe: { label: 'Gewerbeeinheit', icon: Storefront },
  stellplatz: { label: 'Stellplatz', icon: Car },
  sonstiges: { label: 'Einheit', icon: Warehouse },
};

/**
 * Einheit-Detail (README §5.5, §8, Screens 30–38, 54–56).
 * Ein Klick auf ein Widget ersetzt das Raster durch den Bereich (?bereich=…), kein Morph.
 * Beim Wechsel der Einheit wird die Seite neu aufgebaut (key im Wrapper) – der Bereich schließt sich.
 */
export default function EinheitSeite({ objektId, einheitId }: {
  objektId: string;
  einheitId: string;
  onNavigateToMieter?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { zustand, neuLaden } = useEinheitDetail(objektId, einheitId);
  const [ausgeblendet, setAusgeblendet] = useWidgetSichtbarkeit();
  const [filter, setFilter] = useState<GruppenFilter>('alle');
  const [bearbeiten, setBearbeiten] = useState(false);
  // Formulare (Mietverhältnis, Einheit): Rückfrage bei ungespeicherten Änderungen (Escape / „Alle Widgets“)
  const mvVerlassen = useRef<(() => void) | null>(null);
  const [loeschen, setLoeschen] = useState(false);
  const [loeschtGerade, setLoeschtGerade] = useState(false);
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null);
  const [zimmerSignal, setZimmerSignal] = useState(0);

  const roh = params.get('bereich');
  const bereich: EinheitBereichKey | null = istBereichKey(roh) && (ANGEBUNDENE_BEREICHE.has(roh) || roh === 'edit' || roh === 'mvedit') ? roh : null;
  const mvMieterId = params.get('mieter');

  const oeffneBereich = useCallback((key: EinheitBereichKey) => {
    setParams(new URLSearchParams({ bereich: key }));
    window.scrollTo({ top: 0 });
  }, [setParams]);
  const schliesseBereich = useCallback(() => setParams(new URLSearchParams()), [setParams]);
  const bearbeiteMietverhaeltnis = useCallback((m: MietverhaeltnisRoh, reiter?: 'mieter' | 'vertrag' | 'miete' | 'einzug') => {
    setParams(new URLSearchParams({ bereich: 'mvedit', mieter: m.id, ...(reiter ? { reiter } : {}) }));
    window.scrollTo({ top: 0 });
  }, [setParams]);
  // Nach Speichern/Abbrechen dorthin zurück, wo das Formular geöffnet wurde
  const mvFertig = useCallback(() => {
    mvVerlassen.current = null;
    if (location.key !== 'default') navigate(-1); else schliesseBereich();
  }, [location.key, navigate, schliesseBereich]);

  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const heute = heuteIso();

  const geaendert = useCallback((...bereiche: (EinheitBereich | 'einheit')[]) => {
    neuLaden(...bereiche);
    meldeObjektGeaendert(objektId);
  }, [neuLaden, objektId]);

  const bearbeiteEinheit = useCallback((reiter?: EinheitReiter) => {
    setParams(new URLSearchParams({ bereich: 'edit', ...(reiter ? { reiter } : {}) }));
    window.scrollTo({ top: 0 });
  }, [setParams]);

  const wert: EinheitSeitenWert | null = useMemo(() => {
    if (!daten || zustand.status !== 'ok') return null;
    const e = daten.einheit;
    const laufende = laufendeMietverhaeltnisse(daten.mieter);
    const art = einheitArt(e.typ, e.bezeichnung);
    return {
      daten,
      fehler: zustand.fehler,
      objektName: objektName(daten.objekt),
      bezeichnung: (e.bezeichnung ?? '').trim() || 'Ohne Bezeichnung',
      artLabel: e.uebergeordnete_einheit_id ? 'Zimmer' : art === 'stellplatz' || art === 'sonstiges' ? (e.typ ?? '').trim() || ART[art].label : ART[art].label,
      verwaltungsart: verwaltungsartVonObjekt(daten.objekt),
      aktuell: laufende[0] ?? null,
      laufende,
      zeitstrahl: mietverhaeltnisse(daten.mieter, heute),
      miete: mieteAktuell(laufende, heute),
      flaeche: flaecheDerEinheit(e),
      heute,
      neuLaden,
      oeffneBereich,
      bearbeiteEinheit,
      bearbeiteMietverhaeltnis,
      geaendert,
    };
  }, [daten, zustand, heute, neuLaden, oeffneBereich, bearbeiteEinheit, geaendert]);


  const loescheEinheit = async () => {
    if (!daten) return;
    setLoeschtGerade(true);
    setLoeschFehler(null);
    const { error } = await supabase.from('objekt_einheiten').delete().eq('id', daten.einheit.id);
    setLoeschtGerade(false);
    if (error) { setLoeschFehler(loeschFehlerText(error.message)); return; }
    setLoeschen(false);
    meldeObjektGeaendert(objektId);
    navigate(`/admin/objekte/${encodeURIComponent(objektId)}/einheiten`, { replace: true });
  };

  if (zustand.status === 'laedt') {
    return (
      <div aria-busy="true" aria-label="Einheit wird geladen" className="flex flex-col" style={{ gap: 16 }}>
        <div className="animate-pulse" style={{ height: 34, width: 280, borderRadius: 17, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 108, borderRadius: 26, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 44, borderRadius: 16, background: 'var(--fill2)' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gridAutoRows: 158, gap: 16 }}>
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="animate-pulse" style={{ borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      </div>
    );
  }
  if (zustand.status === 'fehlt' || zustand.status === 'fehler' || !wert) {
    return (
      <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '48px 20px', gap: 12 }}>
        <WarningCircle weight="fill" size={36} color="var(--bad)" />
        <span style={{ fontSize: 18, fontWeight: 800 }}>{zustand.status === 'fehlt' ? 'Einheit nicht gefunden' : 'Einheit konnte nicht geladen werden'}</span>
        {zustand.status === 'fehler' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{zustand.meldung}</span>}
        <div className="flex" style={{ gap: 8 }}>
          <ImmoButton onClick={() => navigate(`/admin/objekte/${encodeURIComponent(objektId)}/einheiten`)}>Zu den Einheiten</ImmoButton>
          {zustand.status === 'fehler' && <ImmoButton art="akzent" icon={ArrowClockwise} onClick={() => neuLaden()}>Erneut versuchen</ImmoButton>}
        </div>
      </div>
    );
  }

  // Zimmer (#499) haben keine eigenen WEG-Eigentümer – die hängen an der Wohnung
  const istZimmer = !!wert.daten.einheit.uebergeordnete_einheit_id;
  const weg = wert.verwaltungsart === 'WEG' && !istZimmer;
  const defs = EINHEIT_WIDGETS.filter(d => einheitWidgetAngeboten(d.key, {
    weg, vermietet: wert.laufende.length > 0, hatHausgeld: (wert.daten.eigentuemer?.hausgeld_vorschuss ?? null) !== null,
    wohnraum: einheitArt(wert.daten.einheit.typ, wert.daten.einheit.bezeichnung) === 'wohnung',
  }));
  const segmente = [
    { key: 'alle' as const, label: 'Alle', icon: SquaresFour, anzahl: gruppenAnzahl(defs, ausgeblendet, 'u', 'alle') },
    ...GRUPPEN.filter(g => g.key !== 'ueb').map(g => ({ key: g.key as GruppenFilter, label: g.label, icon: GRUPPEN_ICONS[g.key], anzahl: gruppenAnzahl(defs, ausgeblendet, 'u', g.key as GruppenFilter) })),
  ];
  const fehlerBereiche = Object.keys(wert.fehler);
  const IconArt = ART[einheitArt(wert.daten.einheit.typ, wert.daten.einheit.bezeichnung)].icon;
  const kopf = bereich ? BEREICH_KOPF[bereich] : null;
  const objektPfad = `/admin/objekte/${encodeURIComponent(objektId)}`;

  return (
    <EinheitSeitenKontext.Provider value={wert}>
      <div className="flex flex-col" style={{ gap: 16 }}>
        <nav aria-label="Brotkrumen" className="flex items-center flex-wrap min-w-0" style={{ gap: 10 }}>
          <button type="button" onClick={() => navigate('/admin/objekte')} className="immo-glas flex items-center cursor-pointer"
            style={{ height: 34, padding: '0 14px 0 10px', borderRadius: 17, border: 'none', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
            <CaretLeft weight="bold" size={13} />Immobilien
          </button>
          <span className="flex items-center min-w-0 flex-wrap" style={{ gap: 6, fontSize: 13, fontWeight: 600 }}>
            <CaretRight weight="bold" size={10} color="var(--ink3)" />
            <button type="button" onClick={() => navigate(objektPfad)} className="whitespace-nowrap cursor-pointer"
              style={{ border: 'none', background: 'transparent', padding: 0, fontWeight: 600, color: 'var(--acc)' }}>
              {wert.objektName}
            </button>
            <CaretRight weight="bold" size={10} color="var(--ink3)" />
            <span className="whitespace-nowrap">{wert.bezeichnung}</span>
          </span>
        </nav>

        <header className="immo-glas flex flex-wrap items-end justify-between" style={{ borderRadius: 26, padding: '20px 22px', gap: 14 }}>
          <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
            <h1 className="m-0" style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, overflowWrap: 'anywhere' }}>{wert.bezeichnung}</h1>
            <span className="flex items-center flex-wrap" style={{ gap: 8, fontSize: 14, color: 'var(--ink2)' }}>
              <IconArt size={16} aria-hidden />{wert.artLabel}
              <span aria-hidden style={{ color: 'var(--ink3)' }}>·</span>
              <button type="button" onClick={() => navigate(objektPfad)} className="cursor-pointer"
                style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 14, color: 'var(--ink2)' }}>{wert.objektName}</button>
            </span>
          </div>
          <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <KopfKnopf icon={Handshake} onClick={() => (wert.aktuell ? bearbeiteMietverhaeltnis(wert.aktuell) : oeffneBereich('mv'))}>Mietverhältnis</KopfKnopf>
            <KopfKnopf icon={PencilSimple} onClick={() => bearbeiteEinheit()}>Bearbeiten</KopfKnopf>
            <KopfKnopf icon={PaperPlaneTilt} onClick={() => navigate(schreibenPfad({ objektId, mieterIds: wert.laufende.map(m => m.id), eigentuemerId: wert.daten.eigentuemer?.eigentuemer_id ?? null }))}>Schreiben</KopfKnopf>
            <Popover
              label="Weitere Aktionen"
              breite={220}
              ausloeser={({ offen, umschalten, ref }) => (
                <button ref={ref} type="button" aria-label="Weitere Aktionen" aria-haspopup="menu" aria-expanded={offen} onClick={umschalten}
                  className="flex items-center justify-center cursor-pointer"
                  style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: offen ? 'var(--acc-soft)' : 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)', color: offen ? 'var(--acc)' : 'var(--ink)' }}>
                  <DotsThree weight="bold" size={18} />
                </button>
              )}
            >
              {schliessen => (<>
                {!istZimmer && einheitArt(wert.daten.einheit.typ, wert.daten.einheit.bezeichnung) === 'wohnung' && (
                  <MenueEintrag onClick={() => { schliessen(); schliesseBereich(); setZimmerSignal(n => n + 1); }}><DoorOpen size={16} />Zimmer anlegen (WG)</MenueEintrag>
                )}
                <MenueEintrag gefahr onClick={() => { schliessen(); setLoeschFehler(null); setLoeschen(true); }}><Trash size={16} />Einheit löschen</MenueEintrag>
              </>)}
            </Popover>
          </div>
        </header>

        {!bereich && <EinheitZimmer key={wert.daten.einheit.id} objektId={objektId} einheit={wert.daten.einheit} anlegenSignal={zimmerSignal} />}

        {fehlerBereiche.length > 0 && (
          <div role="status" className="flex items-center flex-wrap" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 600 }}>
            <WarningCircle weight="fill" size={18} />
            Einige Bereiche konnten nicht geladen werden ({fehlerBereiche.join(', ')}).
            <button type="button" onClick={() => neuLaden()} className="cursor-pointer" style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, textDecoration: 'underline' }}>Erneut laden</button>
          </div>
        )}

        {kopf && bereich ? (
          <SlidePanel key={bereich} titel={kopf.titel} icon={kopf.icon}
            onZurueck={bereich === 'mvedit' || bereich === 'edit' ? () => (mvVerlassen.current ? mvVerlassen.current() : mvFertig()) : schliesseBereich}>
            {bereich === 'mvedit'
              ? <MietverhaeltnisBereich mieterId={mvMieterId ?? ''} startReiter={params.get('reiter')} onFertig={mvFertig} registriereVerlassen={f => { mvVerlassen.current = f; }} />
              : bereich === 'edit'
                ? <EinheitBearbeitenBereich startReiter={istEinheitReiter(params.get('reiter')) ? params.get('reiter') as EinheitReiter : 'einheit'} onFertig={mvFertig} registriereVerlassen={f => { mvVerlassen.current = f; }} />
                : <BereichInhalt bereich={bereich} />}
          </SlidePanel>
        ) : (<>
          <div className="flex justify-between items-center flex-wrap" style={{ gap: 10 }}>
            <SegmentedControl segmente={segmente} wert={filter} onChange={setFilter} ariaLabel="Widget-Gruppen" />
            <ImmoButton art={bearbeiten ? 'akzent' : 'fuellung'} icon={bearbeiten ? Check : SlidersHorizontal} onClick={() => setBearbeiten(b => !b)} aria-pressed={bearbeiten}
              style={{ fontWeight: 700 }}>
              {bearbeiten ? 'Fertig' : 'Widgets bearbeiten'}
            </ImmoButton>
          </div>

          {bearbeiten && <WidgetBearbeiten scope="u" defs={defs} ausgeblendet={ausgeblendet} onChange={setAusgeblendet} gruppenIcons={GRUPPEN_ICONS} />}

          <div className="flex flex-col" style={{ gap: 16 }}>
            {GRUPPEN.map(g => {
              const liste = widgetsDerGruppe(defs, g.key, ausgeblendet, 'u', filter, bearbeiten);
              if (liste.length === 0) return null;
              return (
                <WidgetGroup key={g.key} titel={g.label} icon={GRUPPEN_ICONS[g.key]} mitUeberschrift={filter === 'alle'}
                  elemente={liste.map(d => ({ key: d.key, groesse: d.groesse, inhalt: <EinheitWidget def={d} gesperrt={bearbeiten} gedimmt={bearbeiten && !!ausgeblendet.u[d.key]} /> }))} />
              );
            })}
          </div>
        </>)}
      </div>

      {loeschen && (
        <BestaetigenDialog
          titel="Einheit löschen?"
          text="Das kann nicht rückgängig gemacht werden."
          detail={<>„{wert.bezeichnung}“ in {wert.objektName}{loeschFehler && <><br /><span style={{ fontSize: 12, color: 'var(--bad)' }}>Nicht gelöscht: {loeschFehler}</span></>}</>}
          bestaetigenLabel="Löschen"
          icon={Trash}
          laeuft={loeschtGerade}
          onBestaetigen={() => { void loescheEinheit(); }}
          onAbbrechen={() => setLoeschen(false)}
        />
      )}
    </EinheitSeitenKontext.Provider>
  );
}
